/**
 * Chat-level tools picker state.
 *
 * Two pieces of state live here:
 *
 * 1. **Web search** — on by default, persisted globally. Mirrors how
 *    Claude treats it as a top-level mode toggle: not a connector, but
 *    surfaced in the same composer popover for one-click control.
 * 2. **Per-toolkit connector toggles** — opt-out style. The stored
 *    shape is the *disabled* set so adding a new connection is enabled
 *    by default ("you connected this, of course Bird should use it").
 *    Per-workspace because connections are workspace-scoped.
 *
 * Persisted to localStorage so the user's preferences survive reloads.
 * Per-workspace partitioning lives inside the store, not in the
 * localStorage key, so React doesn't re-create the store when the
 * workspace switches.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface ChatConnectorsState {
  /** Per-workspace map of toolkit keys the user has explicitly turned off. */
  readonly disabledByWorkspace: Record<string, ReadonlyArray<string>>;
  /** Whether Bird's web_search tool is active for new chat turns. */
  readonly webSearchEnabled: boolean;
  /** True when toolkit is currently enabled in this workspace. */
  isEnabled: (workspaceId: string, toolkitKey: string) => boolean;
  toggle: (workspaceId: string, toolkitKey: string) => void;
  /** Returns the set of toolkit keys currently enabled, given the active list. */
  enabledFromActive: (
    workspaceId: string,
    activeToolkitKeys: ReadonlyArray<string>,
  ) => ReadonlyArray<string>;
  toggleWebSearch: () => void;
}

export const useChatConnectorsStore = create<ChatConnectorsState>()(
  persist(
    (set, get) => ({
      disabledByWorkspace: {},
      webSearchEnabled: true,
      isEnabled: (workspaceId, toolkitKey) => {
        const disabled = get().disabledByWorkspace[workspaceId] ?? [];
        return !disabled.includes(toolkitKey);
      },
      toggle: (workspaceId, toolkitKey) =>
        set((state) => {
          const current = state.disabledByWorkspace[workspaceId] ?? [];
          const isCurrentlyDisabled = current.includes(toolkitKey);
          const next = isCurrentlyDisabled
            ? current.filter((k) => k !== toolkitKey)
            : [...current, toolkitKey];
          return {
            disabledByWorkspace: {
              ...state.disabledByWorkspace,
              [workspaceId]: next,
            },
          };
        }),
      enabledFromActive: (workspaceId, activeToolkitKeys) => {
        const disabled = get().disabledByWorkspace[workspaceId] ?? [];
        return activeToolkitKeys.filter((k) => !disabled.includes(k));
      },
      toggleWebSearch: () => set((state) => ({ webSearchEnabled: !state.webSearchEnabled })),
    }),
    { name: 'chat-connectors-storage' },
  ),
);
